import React, { useState } from 'react';
import { Box, Card, CardHeader, CardContent, Chip, Divider, Grid, Paper, Typography } from '@mui/material';

interface Franchise {
  id: string;
  name: string;
  storeCount: number;
}

interface Store {
  storeNumber: string;
}

interface SelectedStoresSummaryProps {
  selectedStores: Store[];
  selectedFranchises: Franchise[];
}

const MAX_VISIBLE = 50;

export const SelectedStoresSummary: React.FC<SelectedStoresSummaryProps> = ({ selectedStores, selectedFranchises }) => {
  const [selectedFranchiseDetail, setSelectedFranchiseDetail] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  // Filter stores based on selected franchise
  const filteredStores = selectedFranchiseDetail
    ? selectedStores.filter((store) => {
        const franchise = selectedFranchises.find((f) => f.id === selectedFranchiseDetail);
        return franchise ? franchise.storeCount > 0 : false;
      })
    : [];

  const visibleStores = expanded ? filteredStores : filteredStores.slice(0, MAX_VISIBLE);

  const extraCount = filteredStores.length - MAX_VISIBLE;

  if (selectedStores.length === 0) {
    return null;
  }

  return (
    <Card sx={{ mb: 3 }}>
      <CardHeader title='Selected Stores Summary' subheader={`Total: ${selectedStores.length} stores`} />
      {selectedFranchises.length > 0 && (
        <>
          <Divider />
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant='subtitle2' gutterBottom>
                  Franchise Groups:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }} data-testid='selectedFranchiseChips'>
                  {selectedFranchises.slice(0, 5).map((franchise) => (
                    <Chip
                      key={franchise.id}
                      label={`${franchise.name} (${franchise.storeCount} stores)`}
                      color='primary'
                      onClick={() =>
                        setSelectedFranchiseDetail(franchise.id === selectedFranchiseDetail ? null : franchise.id)
                      }
                      variant={franchise.id === selectedFranchiseDetail ? 'filled' : 'outlined'}
                      sx={{ cursor: 'pointer' }}
                    />
                  ))}
                  {selectedFranchises.length > 5 && (
                    <Chip label={`+${selectedFranchises.length - 5} more`} color='primary' />
                  )}
                </Box>
              </Grid>

              {selectedFranchiseDetail !== null && (
                <Grid item xs={12} sx={{ mt: 2 }}>
                  <Paper
                    variant='outlined'
                    sx={{
                      p: 2,
                      backgroundColor: 'rgba(25, 118, 210, 0.05)',
                      borderColor: 'primary.main',
                    }}
                  >
                    <Typography variant='subtitle2' gutterBottom>
                      {selectedFranchises.find((f) => f.id === selectedFranchiseDetail)?.name} Store Numbers:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {visibleStores.map((store) => (
                        <Chip
                          key={store.storeNumber}
                          label={store.storeNumber}
                          size='small'
                          variant='outlined'
                          color='primary'
                        />
                      ))}

                      {!expanded && extraCount > 0 && (
                        <Chip
                          label={`+${extraCount}`}
                          size='small'
                          variant='outlined'
                          color='primary'
                          onClick={() => setExpanded(true)}
                          sx={{ cursor: 'pointer' }}
                        />
                      )}
                      {expanded && filteredStores.length > MAX_VISIBLE && (
                        <Chip
                          label='Show less'
                          size='small'
                          variant='outlined'
                          color='primary'
                          onClick={() => setExpanded(false)}
                          sx={{ cursor: 'pointer' }}
                        />
                      )}
                    </Box>
                  </Paper>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </>
      )}
    </Card>
  );
};

export default SelectedStoresSummary;
